import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { PixTransaction, BankSlip, Transfer, ReportFilter } from '@/types';
import { pixApi, boletosApi, transfersApi } from '@/services/api';

interface TransactionsState {
  pix: {
    items: PixTransaction[];
    isLoading: boolean;
    error: string | null;
    pagination: {
      page: number;
      limit: number;
      total: number;
      hasMore: boolean;
    };
  };
  boletos: {
    items: BankSlip[];
    isLoading: boolean;
    error: string | null;
    pagination: {
      page: number;
      limit: number;
      total: number;
      hasMore: boolean;
    };
  };
  transfers: {
    items: Transfer[];
    isLoading: boolean;
    error: string | null;
    pagination: {
      page: number;
      limit: number;
      total: number;
      hasMore: boolean;
    };
  };
  filters: ReportFilter;
  selectedTransaction: PixTransaction | BankSlip | Transfer | null;
}

const initialPagination = {
  page: 1,
  limit: 20,
  total: 0,
  hasMore: false,
};

const initialState: TransactionsState = {
  pix: {
    items: [],
    isLoading: false,
    error: null,
    pagination: { ...initialPagination },
  },
  boletos: {
    items: [],
    isLoading: false,
    error: null,
    pagination: { ...initialPagination },
  },
  transfers: {
    items: [],
    isLoading: false,
    error: null,
    pagination: { ...initialPagination },
  },
  filters: {},
  selectedTransaction: null,
};

// PIX Async Thunks
export const fetchPixTransactions = createAsyncThunk(
  'transactions/fetchPixTransactions',
  async (params?: any, { rejectWithValue }) => {
    try {
      const response = await pixApi.listTransactions(params);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Erro ao carregar transações PIX');
      }
      return { data: response.data, pagination: response.pagination };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Erro ao carregar transações PIX');
    }
  }
);

export const createPixCharge = createAsyncThunk(
  'transactions/createPixCharge',
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await pixApi.createCharge(data);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Erro ao criar cobrança PIX');
      }
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Erro ao criar cobrança PIX');
    }
  }
);

export const createPixPayment = createAsyncThunk(
  'transactions/createPixPayment',
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await pixApi.createPayment(data);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Erro ao realizar pagamento PIX');
      }
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Erro ao realizar pagamento PIX');
    }
  }
);

// Boletos Async Thunks
export const fetchBoletos = createAsyncThunk(
  'transactions/fetchBoletos',
  async (params?: any, { rejectWithValue }) => {
    try {
      const response = await boletosApi.list(params);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Erro ao carregar boletos');
      }
      return { data: response.data, pagination: response.pagination };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Erro ao carregar boletos');
    }
  }
);

export const createBoleto = createAsyncThunk(
  'transactions/createBoleto',
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await boletosApi.create(data);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Erro ao criar boleto');
      }
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Erro ao criar boleto');
    }
  }
);

// Transfers Async Thunks
export const fetchTransfers = createAsyncThunk(
  'transactions/fetchTransfers',
  async (params?: any, { rejectWithValue }) => {
    try {
      const response = await transfersApi.list(params);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Erro ao carregar transferências');
      }
      return { data: response.data, pagination: response.pagination };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Erro ao carregar transferências');
    }
  }
);

export const createTransfer = createAsyncThunk(
  'transactions/createTransfer',
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await transfersApi.create(data);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Erro ao criar transferência');
      }
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Erro ao criar transferência');
    }
  }
);

const transactionsSlice = createSlice({
  name: 'transactions',
  initialState,
  reducers: {
    // Filters
    setFilters: (state, action: PayloadAction<ReportFilter>) => {
      state.filters = action.payload;
    },
    clearFilters: (state) => {
      state.filters = {};
    },

    // Selected transaction
    setSelectedTransaction: (state, action: PayloadAction<PixTransaction | BankSlip | Transfer | null>) => {
      state.selectedTransaction = action.payload;
    },

    // Clear errors
    clearPixError: (state) => {
      state.pix.error = null;
    },
    clearBoletosError: (state) => {
      state.boletos.error = null;
    },
    clearTransfersError: (state) => {
      state.transfers.error = null;
    },

    // Add new transactions (for real-time updates)
    addPixTransaction: (state, action: PayloadAction<PixTransaction>) => {
      state.pix.items.unshift(action.payload);
    },
    addBoleto: (state, action: PayloadAction<BankSlip>) => {
      state.boletos.items.unshift(action.payload);
    },
    addTransfer: (state, action: PayloadAction<Transfer>) => {
      state.transfers.items.unshift(action.payload);
    },

    // Update transaction status (for real-time updates)
    updatePixTransaction: (state, action: PayloadAction<{ id: string; updates: Partial<PixTransaction> }>) => {
      const { id, updates } = action.payload;
      const index = state.pix.items.findIndex(item => item.id === id);
      if (index !== -1) {
        state.pix.items[index] = { ...state.pix.items[index], ...updates };
      }
    },
    updateBoleto: (state, action: PayloadAction<{ id: string; updates: Partial<BankSlip> }>) => {
      const { id, updates } = action.payload;
      const index = state.boletos.items.findIndex(item => item.id === id);
      if (index !== -1) {
        state.boletos.items[index] = { ...state.boletos.items[index], ...updates };
      }
    },
    updateTransfer: (state, action: PayloadAction<{ id: string; updates: Partial<Transfer> }>) => {
      const { id, updates } = action.payload;
      const index = state.transfers.items.findIndex(item => item.id === id);
      if (index !== -1) {
        state.transfers.items[index] = { ...state.transfers.items[index], ...updates };
      }
    },
  },
  extraReducers: (builder) => {
    // PIX Transactions
    builder
      .addCase(fetchPixTransactions.pending, (state) => {
        state.pix.isLoading = true;
        state.pix.error = null;
      })
      .addCase(fetchPixTransactions.fulfilled, (state, action) => {
        state.pix.isLoading = false;
        const { data, pagination } = action.payload;
        
        if (pagination?.page === 1) {
          state.pix.items = data;
        } else {
          state.pix.items.push(...data);
        }
        
        if (pagination) {
          state.pix.pagination = {
            page: pagination.page,
            limit: pagination.perPage,
            total: pagination.total,
            hasMore: pagination.hasNext,
          };
        }
      })
      .addCase(fetchPixTransactions.rejected, (state, action) => {
        state.pix.isLoading = false;
        state.pix.error = action.payload as string;
      })
      .addCase(createPixCharge.fulfilled, (state, action) => {
        state.pix.items.unshift(action.payload);
      })
      .addCase(createPixPayment.fulfilled, (state, action) => {
        state.pix.items.unshift(action.payload);
      });

    // Boletos
    builder
      .addCase(fetchBoletos.pending, (state) => {
        state.boletos.isLoading = true;
        state.boletos.error = null;
      })
      .addCase(fetchBoletos.fulfilled, (state, action) => {
        state.boletos.isLoading = false;
        const { data, pagination } = action.payload;
        
        if (pagination?.page === 1) {
          state.boletos.items = data;
        } else {
          state.boletos.items.push(...data);
        }
        
        if (pagination) {
          state.boletos.pagination = {
            page: pagination.page,
            limit: pagination.perPage,
            total: pagination.total,
            hasMore: pagination.hasNext,
          };
        }
      })
      .addCase(fetchBoletos.rejected, (state, action) => {
        state.boletos.isLoading = false;
        state.boletos.error = action.payload as string;
      })
      .addCase(createBoleto.fulfilled, (state, action) => {
        state.boletos.items.unshift(action.payload);
      });

    // Transfers
    builder
      .addCase(fetchTransfers.pending, (state) => {
        state.transfers.isLoading = true;
        state.transfers.error = null;
      })
      .addCase(fetchTransfers.fulfilled, (state, action) => {
        state.transfers.isLoading = false;
        const { data, pagination } = action.payload;
        
        if (pagination?.page === 1) {
          state.transfers.items = data;
        } else {
          state.transfers.items.push(...data);
        }
        
        if (pagination) {
          state.transfers.pagination = {
            page: pagination.page,
            limit: pagination.perPage,
            total: pagination.total,
            hasMore: pagination.hasNext,
          };
        }
      })
      .addCase(fetchTransfers.rejected, (state, action) => {
        state.transfers.isLoading = false;
        state.transfers.error = action.payload as string;
      })
      .addCase(createTransfer.fulfilled, (state, action) => {
        state.transfers.items.unshift(action.payload);
      });
  },
});

export const {
  setFilters,
  clearFilters,
  setSelectedTransaction,
  clearPixError,
  clearBoletosError,
  clearTransfersError,
  addPixTransaction,
  addBoleto,
  addTransfer,
  updatePixTransaction,
  updateBoleto,
  updateTransfer,
} = transactionsSlice.actions;

export default transactionsSlice.reducer;